import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  CgWebsite,
  CgFigma,
  CgBrowser
} from 'react-icons/cg';
import { 
  AiOutlineApi,
  AiOutlineCloud,
  AiOutlineRobot,
  AiOutlineShop, 
  AiOutlineShoppingCart,
  AiOutlineDatabase,
  AiOutlineDashboard,
  AiOutlinePartition 
} from 'react-icons/ai';
import {
  BsDatabase,
  BsGear,
  BsBuilding
} from 'react-icons/bs';
import ServiceModal from './ServiceModal';
import { useLocation } from 'react-router-dom';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ServicesContainer = styled.div`
//   max-width: 1400px;
  margin: 0 auto;
  padding: 6rem 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(180deg, rgba(66, 84, 181, 0.05) 0%, rgba(255, 255, 255, 0) 100%);
    z-index: -1;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: #333;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #4254b5, #28a745);
    border-radius: 2px;
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  margin-top: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  animation: ${fadeIn} 0.5s ease-out forwards;
  animation-delay: ${props => props.delay}s;
  opacity: 0;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  scroll-margin-top: 100px;
  scroll-behavior: smooth;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => props.accentColor};
    transition: width 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: ${props => props.accentColor};
    opacity: 0.05;
    border-radius: 50%;
    transform: translate(50%, -50%);
  }
  
  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    
    &::before {
      width: 100%;
      opacity: 0.1;
    }
  }
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => `${props.accentColor}15`};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 30px;
    height: 30px;
    color: ${props => props.accentColor};
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #333;
  position: relative;
  padding-bottom: 1rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background: ${props => props.accentColor};
    border-radius: 1px;
  }
`;

const ServiceDescription = styled.p`
  color: #666;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    margin: 0.8rem 0;
    padding-left: 1.8rem;
    position: relative;
    color: #555;
    font-size: 1rem;
    
    &::before {
      content: '→';
      position: absolute;
      left: 0;
      color: ${props => props.accentColor};
      font-weight: bold;
      transform: translateY(-2px);
    }
    
    &:hover {
      color: ${props => props.accentColor};
      transform: translateX(5px);
      transition: all 0.3s ease;
    }
  }
`;

const ServiceCategory = styled.div`
  margin-bottom: 4rem;
`;

const CategoryTitle = styled.h2`
  font-size: 2rem;
  color: #333;
  margin-bottom: 2rem;
  text-align: left;
  position: relative;
  padding-left: 1rem;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #4254b5, #28a745);
    border-radius: 2px;
  }
`;

const generateServiceId = (categoryName, serviceTitle) => {
  return `${categoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${serviceTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
};

const Web2Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Handle scroll to element when hash changes
    if (location.hash) {
      // Remove the '#' from the hash
      const id = location.hash.substring(1);
      
      // Add a small delay to ensure the elements are rendered
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    }
  }, [location]);

  const categories = [
    {
      name: "AI & Automation",
      services: [
        {
          title: 'AI Agent Development',
          description: 'Intelligent automation solutions for your business',
          features: [
            'Custom AI Agent or assistant to boost your business productivity',
            'Customer Service',
            'Machine Learning integration',
            'Automated Data Analysis and decision making'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "Customer Service",
                capabilities: [
                  "24/7 customer support automation with NLP",
                  "Multi-language support and real-time translation",
                  "Smart ticket routing and priority management",
                  "Personalized customer interaction history tracking",
                  "Automated response suggestions"
                ]
              },
              {
                category: "Process Automation",
                capabilities: [
                  "Document processing and data extraction",
                  "Automated workflow management",
                  "Real-time monitoring and reporting",
                  "Intelligent form filling and validation",
                  "Automated scheduling and calendar management"
                ]
              },
              {
                category: "Data Analysis & decision making",
                capabilities: [
                  "Predictive analytics for business metrics",
                  "Customer behavior pattern recognition",
                  "Market trend analysis and forecasting",
                  "Anomaly detection in operations",
                  "Automated report generation"
                ]
              }
            ],
            benefits: [
              "Reduced operational costs through automation",
              "Improved customer satisfaction and response times",
              "Enhanced decision-making through data-driven insights",
              "Increased operational efficiency",
              "24/7 operational capability"
            ],
            customization_options: {
              scalability: "Enterprise-ready",
              deployment: ["Cloud", "On-premise", "Hybrid"],
              integration_level: "Full-stack integration available",
              training_support: "Comprehensive onboarding included"
            }
          },
          accentColor: '#e83e8c',
          icon: <AiOutlineRobot />
        },
      ]
    },
    {
      name: "Cloud & Infrastructure",
      services: [
        {
          title: 'Cloud Hosting Solutions',
          description: 'Reliable and scalable cloud infrastructure',
          features: [
            'AWS and Azure expertise',
            'Cloud architecture design',
            'Auto-scaling solutions',
            'Security implementation'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "Infrastructure Management",
                capabilities: [
                  "Multi-cloud architecture design",
                  "Auto-scaling and load balancing",
                  "Disaster recovery solutions",
                  "High-availability systems",
                  "Container orchestration"
                ]
              },
              {
                category: "Security & Compliance",
                capabilities: [
                  "Advanced firewall configuration",
                  "Identity and access management",
                  "Data encryption protocols",
                  "Compliance monitoring",
                  "Security incident response"
                ]
              },
              {
                category: "Performance Optimization",
                capabilities: [
                  "Resource utilization monitoring",
                  "Cost optimization strategies",
                  "Performance benchmarking",
                  "Cache optimization",
                  "Database tuning"
                ]
              }
            ],
            benefits: [
              "Reduced infrastructure costs",
              "Improved scalability",
              "Enhanced security",
              "Better disaster recovery",
              "Global availability"
            ],
            customization_options: {
              cloud_providers: ["AWS", "Azure", "Google Cloud"],
              deployment_models: ["Public", "Private", "Hybrid"],
              service_level: "24/7 support",
              monitoring: "Real-time alerts"
            }
          },
          accentColor: '#6610f2',
          icon: <AiOutlineCloud />
        },
        {
          title: 'API Development',
          description: 'Secure and efficient APIs for seamless integration',
          features: [
            'RESTful API design',
            'GraphQL implementation',
            'API documentation',
            'Performance optimization'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "API Architecture",
                capabilities: [
                  "RESTful and GraphQL API design patterns",
                  "Microservices architecture implementation",
                  "API versioning and lifecycle management",
                  "Custom endpoint development",
                  "Real-time WebSocket integration"
                ]
              },
              {
                category: "Security & Authentication",
                capabilities: [
                  "OAuth2 and JWT implementation",
                  "Rate limiting and throttling",
                  "API key management system",
                  "Role-based access control (RBAC)",
                  "Security audit logging"
                ]
              },
              {
                category: "Documentation & Testing",
                capabilities: [
                  "Interactive API documentation (Swagger/OpenAPI)",
                  "Automated testing suites",
                  "Load testing and performance optimization",
                  "Comprehensive error handling",
                  "API monitoring and analytics"
                ]
              }
            ],
            benefits: [
              "Seamless system integration",
              "Enhanced data security",
              "Improved scalability",
              "Reduced development time",
              "Better system interoperability"
            ],
            customization_options: {
              api_types: ["REST", "GraphQL", "WebSocket"],
              authentication: ["OAuth2", "JWT", "API Keys"],
              documentation: "Interactive API documentation",
              support: "24/7 API support"
            }
          },
          accentColor: '#fd7e14',
          icon: <AiOutlineApi />
        },
      ]
    },
    {
      name: "Collaboration & Productivity Tools",
      services: [
        {
          title: 'Workflow Automation',
          description: 'Streamline your business processes',
          features: [
            'Business process automation',
            'Custom workflow design',
            'Integration with existing systems',
            'Performance monitoring'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "Process Design",
                capabilities: [
                  "Visual workflow builder interface",
                  "Custom trigger and action creation",
                  "Conditional logic implementation",
                  "Multi-step workflow orchestration",
                  "Process templating and reuse"
                ]
              },
              {
                category: "Integration & Connectivity",
                capabilities: [
                  "Third-party system integration",
                  "Custom API connections",
                  "Database synchronization",
                  "Legacy system compatibility",
                  "Cross-platform workflow execution"
                ]
              },
              {
                category: "Monitoring & Analytics",
                capabilities: [
                  "Real-time process monitoring",
                  "Performance analytics dashboard",
                  "Bottleneck identification",
                  "ROI tracking and reporting",
                  "Process optimization insights"
                ]
              }
            ],
            benefits: [
              "Increased operational efficiency",
              "Reduced manual errors",
              "Improved process visibility",
              "Significant cost savings",
              "Better resource allocation"
            ],
            customization_options: {
              deployment: ["Cloud", "On-premise", "Hybrid"],
              integrations: "200+ pre-built connectors",
              automation_level: "Full process automation",
              support: "24/7 technical support"
            }
          },
          accentColor: '#17a2b8',
          icon: <BsGear />
        },
      ]
    },
    {
      name: "Digital Marketing & Analytics",
      services: [
        {
          title: 'Data Science Services',
          description: 'Transform data into actionable insights',
          features: [
            'Data analysis and visualization',
            'Machine learning models',
            'Predictive analytics',
            'Business intelligence'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "Data Analytics & Visualization",
                capabilities: [
                  "Advanced statistical analysis and modeling",
                  "Interactive data visualization dashboards",
                  "Real-time data processing and analytics",
                  "Custom reporting solutions",
                  "Big data processing and management"
                ]
              },
              {
                category: "Machine Learning Solutions",
                capabilities: [
                  "Predictive modeling and forecasting",
                  "Customer segmentation and behavior analysis",
                  "Anomaly detection systems",
                  "Natural Language Processing (NLP)",
                  "Computer Vision applications"
                ]
              },
              {
                category: "Business Intelligence",
                capabilities: [
                  "KPI tracking and monitoring",
                  "Automated insight generation",
                  "Competitive analysis tools",
                  "Market trend analysis",
                  "Decision support systems"
                ]
              }
            ],
            benefits: [
              "Data-driven decision making",
              "Improved business forecasting",
              "Enhanced customer insights",
              "Automated reporting workflows",
              "Competitive market advantage"
            ],
            customization_options: {
              data_sources: ["Structured", "Unstructured", "Real-time"],
              deployment: ["Cloud", "On-premise", "Hybrid"],
              integration: "Full stack data pipeline",
              scalability: "Enterprise-grade infrastructure"
            }
          },
          accentColor: '#20c997',
          icon: <BsDatabase />
        },
      ]
    },
    {
      name: "Enterprise Internal Systems",
      services: [
        {
          title: 'ERP Solutions',
          description: 'Comprehensive enterprise resource planning systems',
          features: [
            'Custom ERP development',
            'System integration',
            'Resource optimization',
            'Real-time reporting'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "Core Business Functions",
                capabilities: [
                  "Financial management and accounting",
                  "Inventory and supply chain management",
                  "Human resources and payroll",
                  "Customer relationship management",
                  "Project and resource planning"
                ]
              },
              {
                category: "Integration & Analytics",
                capabilities: [
                  "Cross-department data integration",
                  "Real-time reporting and analytics",
                  "Business intelligence dashboards",
                  "Automated workflow management",
                  "Document management system"
                ]
              },
              {
                category: "Security & Compliance",
                capabilities: [
                  "Role-based access control",
                  "Audit trail and compliance reporting",
                  "Data encryption and security",
                  "Backup and disaster recovery",
                  "Regulatory compliance management"
                ]
              }
            ],
            benefits: [
              "Streamlined business operations",
              "Improved data accuracy and consistency",
              "Enhanced productivity and efficiency",
              "Better decision-making capabilities",
              "Reduced operational costs"
            ],
            customization_options: {
              modules: ["Finance", "HR", "SCM", "CRM", "Project Management"],
              deployment: ["Cloud", "On-premise", "Hybrid"],
              integration: "Full system integration available",
              support: "24/7 technical support"
            }
          },
          accentColor: '#6f42c1',
          icon: <BsBuilding />
        },
      ]
    },
    {
      name: "Web Development & Design",
      services: [
        {
          title: 'CMS Website Development',
          description: 'Custom Content Management Systems tailored to your business needs',
          features: [
            'WordPress, Drupal, and custom CMS solutions',
            'User-friendly admin interfaces',
            'Content workflow management',
            'SEO-optimized architecture'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "Content Management",
                capabilities: [
                  "Intuitive content editing interface",
                  "Media library and asset management",
                  "Version control and content scheduling",
                  "Multi-user collaboration tools",
                  "Custom content types and fields"
                ]
              },
              {
                category: "Technical Features",
                capabilities: [
                  "Responsive design implementation",
                  "SEO optimization tools",
                  "Performance optimization",
                  "Security hardening",
                  "API integrations"
                ]
              },
              {
                category: "E-commerce & Marketing",
                capabilities: [
                  "E-commerce functionality",
                  "Marketing automation tools",
                  "Analytics integration",
                  "Social media integration",
                  "Email marketing tools"
                ]
              }
            ],
            benefits: [
              "Easy content management",
              "Improved website performance",
              "Enhanced SEO capabilities",
              "Scalable architecture",
              "Reduced maintenance costs"
            ],
            customization_options: {
              platforms: ["WordPress", "Drupal", "GovCMS"],
              features: "Modular architecture",
              hosting: ["Managed", "Self-hosted"],
              support: "Ongoing maintenance available"
            }
          },
          accentColor: '#4254b5',
          icon: <CgWebsite />
        },
        {
          title: 'UI/UX Design',
          description: 'Creating intuitive and engaging user experiences',
          features: [
            'User research and personas',
            'Wireframing and prototyping',
            'Interactive design',
            'Usability testing'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "User Research",
                capabilities: [
                  "User persona development",
                  "User journey mapping",
                  "Usability testing",
                  "Competitive analysis",
                  "User feedback integration"
                ]
              },
              {
                category: "Design Process",
                capabilities: [
                  "Information architecture",
                  "Wireframing and prototyping",
                  "Visual design systems",
                  "Interactive prototypes",
                  "Responsive design"
                ]
              },
              {
                category: "Implementation",
                capabilities: [
                  "Design system creation",
                  "Component library development",
                  "Animation and micro-interactions",
                  "Accessibility compliance",
                  "Cross-platform optimization"
                ]
              }
            ],
            benefits: [
              "Enhanced user experience",
              "Increased user engagement",
              "Higher conversion rates",
              "Reduced development time",
              "Consistent brand experience"
            ],
            customization_options: {
              deliverables: ["Wireframes", "Prototypes", "Design Systems"],
              tools: ["Figma", "Adobe XD", "Sketch"],
              collaboration: "Real-time design collaboration",
              iterations: "Unlimited revisions"
            }
          },
          accentColor: '#28a745',
          icon: <CgFigma />
        },
        {
          title: 'Dynamic Web Applications',
          description: 'Robust and scalable web applications for modern businesses',
          features: [
            'React.js and Next.js development',
            'Real-time data processing',
            'Progressive Web Apps (PWA)',
            'Cross-platform compatibility'
          ],
          modalContent: {
            detailedFeatures: [
              {
                category: "Frontend Development",
                capabilities: [
                  "React.js component architecture",
                  "Next.js server-side rendering",
                  "State management solutions",
                  "Progressive Web App features",
                  "Responsive UI implementation"
                ]
              },
              {
                category: "Backend Integration",
                capabilities: [
                  "RESTful API development",
                  "Database architecture",
                  "Authentication systems",
                  "Real-time data processing",
                  "Cloud service integration"
                ]
              },
              {
                category: "Performance & Security",
                capabilities: [
                  "Load time optimization",
                  "Security best practices",
                  "Scalability planning",
                  "Monitoring and analytics",
                  "Automated testing"
                ]
              }
            ],
            benefits: [
              "Enhanced user experience",
              "Faster load times",
              "Better SEO performance",
              "Cross-platform compatibility",
              "Scalable architecture"
            ],
            customization_options: {
              frontend: ["React.js", "Next.js", "Redux"],
              backend: ["Node.js", "Python", "Java"],
              deployment: ["Vercel", "AWS", "GCP"],
              support: "Continuous maintenance"
            }
          },
          accentColor: '#dc3545',
          icon: <CgBrowser />
        },
      ]
    },
  ];

  return (
    <ServicesContainer>
      <SectionTitle>Our Web2 Development Services</SectionTitle>
      {categories.map((category, categoryIndex) => (
        <ServiceCategory key={categoryIndex}>
          <CategoryTitle>{category.name}</CategoryTitle>
          <ServiceGrid>
            {category.services.map((service, serviceIndex) => {
              const serviceId = generateServiceId(category.name, service.title);
              return (
                <ServiceCard 
                  key={serviceIndex}
                  id={serviceId}
                  delay={serviceIndex * 0.1}
                  accentColor={service.accentColor}
                  onClick={() => setSelectedService(service)}
                >
                  <ServiceIcon accentColor={service.accentColor}>
                    {service.icon}
                  </ServiceIcon>
                  <ServiceTitle accentColor={service.accentColor}>
                    {service.title}
                  </ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                  <ServiceFeatures accentColor={service.accentColor}>
                    {service.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ServiceFeatures>
                </ServiceCard>
              );
            })}
          </ServiceGrid>
        </ServiceCategory>
      ))}
      
      {selectedService && (
        <ServiceModal 
          service={selectedService} 
          onClose={() => setSelectedService(null)}
        />
      )}
    </ServicesContainer>
  );
};

export default Web2Services;